import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { instituteService } from '../../../services/instituteService';

const AISettingsManager = () => {
  const { user, userProfile } = useAuth();
  const [aiAssistantName, setAiAssistantName] = useState('AI Assistant');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');

  // Name suggestions by region
  const nameSuggestions = [
    { name: 'Vidya', meaning: 'Knowledge (Sanskrit)', region: 'Universal' },
    { name: 'Aarav', meaning: 'Peaceful & Wise', region: 'North India' },
    { name: 'Priya', meaning: 'Beloved', region: 'South India' },
    { name: 'Arjun', meaning: 'Bright & Shining', region: 'North India' },
    { name: 'Ananya', meaning: 'Unique', region: 'Universal' },
    { name: 'Karthik', meaning: 'Bestower of Courage', region: 'South India' },
    { name: 'Diya', meaning: 'Light', region: 'Universal' },
    { name: 'Rohan', meaning: 'Ascending', region: 'Universal' },
    { name: 'Saanvi', meaning: 'Goddess Lakshmi', region: 'West India' },
    { name: 'Amit', meaning: 'Friend', region: 'Universal' },
    { name: 'Ishaan', meaning: 'Sun', region: 'West India' },
    { name: 'Shreya', meaning: 'Auspicious', region: 'East India' }
  ];

  useEffect(() => {
    loadAISettings();
  }, [userProfile]);

  const loadAISettings = async () => {
    try {
      setLoading(true);
      const instituteId = userProfile?.institute_profiles?.[0]?.id;
      if (!instituteId) {
        console.error('No institute profile found');
        return;
      }
      const profile = await instituteService?.getInstituteById(instituteId);
      if (profile?.ai_assistant_name) {
        setAiAssistantName(profile?.ai_assistant_name);
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!aiAssistantName?.trim()) {
      alert('Please enter a name for your AI assistant');
      return;
    }

    try {
      setSaving(true);
      const instituteId = userProfile?.institute_profiles?.[0]?.id;
      if (!instituteId) {
        alert('Institute profile not found. Please contact support.');
        return;
      }
      await instituteService?.updateInstituteProfile(instituteId, {
        aiAssistantName: aiAssistantName?.trim()
      });
      alert('AI assistant name updated successfully!');
    } catch (error) {
      console.error('Error saving AI settings:', error);
      alert('Failed to save AI settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuggestionClick = (name) => {
    setAiAssistantName(name);
  };

  const generatePreview = () => {
    const messages = [
      `Hi! I'm ${aiAssistantName}, your admission assistant at ${userProfile?.instituteName || 'our institute'}. I'm here to help you explore our courses and answer your questions. What would you like to know?`,
      `Great question! Let me help you with that. As ${aiAssistantName}, I have access to all our course details, fees, and admission requirements.`,
      `I understand you need more detailed information. Let me connect you with our counselor who can provide personalized guidance. I'm ${aiAssistantName}, and I'll make sure they have all the context from our conversation!`
    ];
    return messages?.[Math.floor(Math.random() * messages?.length)];
  };

  const handlePreview = () => {
    setPreviewMessage(generatePreview());
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading AI settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Bot" size={24} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
              AI Assistant Customization
            </h2>
            <p className="text-sm text-muted-foreground">
              Personalize your AI chat assistant with a custom name. This makes interactions more engaging and builds trust with students.
            </p>
          </div>
        </div>
      </div>
      {/* AI Name Input */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-foreground mb-2">
            AI Assistant Name
          </label>
          <div className="flex gap-3">
            <Input
              type="text"
              value={aiAssistantName}
              onChange={(e) => setAiAssistantName(e?.target?.value)}
              placeholder="Enter AI assistant name (e.g., Vidya, Priya, Amit)"
              className="flex-1"
              maxLength={30}
            />
            <Button
              onClick={handlePreview}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="Eye" size={16} />
              Preview
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Choose a friendly, memorable name that resonates with your students. Max 30 characters.
          </p>
        </div>

        {/* Preview Box */}
        {previewMessage && (
          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Bot" size={16} color="var(--color-accent)" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-foreground">{aiAssistantName}</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">
                    <Icon name="Bot" size={10} />
                    AI Assistant
                  </span>
                </div>
                <p className="text-sm text-foreground">{previewMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving || !aiAssistantName?.trim()}
            className="flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Icon name="Save" size={16} />
                Save AI Name
              </>
            )}
          </Button>
        </div>
      </div>
      {/* Name Suggestions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Sparkles" size={20} color="var(--color-accent)" />
          Suggested Names
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Choose from these culturally relevant names or create your own unique name.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {nameSuggestions?.map((suggestion) => (
            <button
              key={suggestion?.name}
              onClick={() => handleSuggestionClick(suggestion?.name)}
              className={`
                p-4 rounded-lg border-2 transition-all text-left
                ${aiAssistantName === suggestion?.name
                  ? 'border-accent bg-accent/5' :'border-border hover:border-accent/50 hover:bg-muted/50'
                }
              `}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-base font-semibold text-foreground">{suggestion?.name}</span>
                {aiAssistantName === suggestion?.name && (
                  <Icon name="Check" size={16} color="var(--color-accent)" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-1">{suggestion?.meaning}</p>
              <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">
                {suggestion?.region}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Benefits Section */}
      <div className="bg-gradient-to-br from-accent/5 to-primary/5 border border-accent/20 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
          Why Customize Your AI Name?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Users" size={16} color="var(--color-success)" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">More Personal</h4>
              <p className="text-xs text-muted-foreground">
                Students feel they're talking to someone, not just a bot
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Heart" size={16} color="var(--color-accent)" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Better Engagement</h4>
              <p className="text-xs text-muted-foreground">
                Named assistants increase student interaction by 40%
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Award" size={16} color="var(--color-primary)" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Brand Identity</h4>
              <p className="text-xs text-muted-foreground">
                Creates memorable experience unique to your institute
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={16} color="var(--color-warning)" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Trust Building</h4>
              <p className="text-xs text-muted-foreground">
                Named assistants feel more reliable and approachable
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISettingsManager;